import {
  API,
  deleteRequest,
  getRequest,
  getRequestBlob,
  patchRequest,
  postRequest,
  putRequest,
} from '@/api/api';
import type { FileDto } from '@/api/api.dto';
import { invalidateAuthorizedFile } from '@/utils/file-links';

import type {
  ActivityGenerator,
  CreateActivityGeneratorPayload,
  CreateMaintenancePayload,
  CreateVesselDto,
  DailyLog,
  Equipment,
  Maintenance,
  UpdateVesselDto,
  Vessel,
} from './vessel.dto';

export const createVessel = async (payload: CreateVesselDto): Promise<Vessel> => {
  const response = await postRequest<Vessel, CreateVesselDto>(API.VESSEL, '', payload);
  return response.data;
};

export const updateVessel = async (
  payload: UpdateVesselDto,
  vesselId?: string,
): Promise<Vessel> => {
  const response = await putRequest<Vessel, UpdateVesselDto>(
    API.VESSEL,
    '',
    payload,
    false,
    vesselId,
  );
  return response.data;
};

export const setVesselImage = async (imageFile: File, vesselId?: string): Promise<FileDto> => {
  const formData = new FormData();
  formData.append('image', imageFile);
  const response = await patchRequest<FileDto, FormData>(
    API.VESSEL,
    '/image',
    formData,
    false,
    vesselId,
  );
  // The link addresses the vessel, not the file, so it comes back unchanged.
  invalidateAuthorizedFile(response.data.link);
  return response.data;
};

export const getMyVessels = async (ownerId: string): Promise<Vessel[]> => {
  const response = await getRequest<{ items: Vessel[]; count: number }>(API.VESSEL, '', false, {
    page: 1,
    limit: 10,
    owner_id: ownerId,
  });

  return response.data.items;
};

export const getVesselsByIds = async (vesselIds: string[]): Promise<Vessel[]> => {
  if (!vesselIds.length) return [];

  const response = await getRequest<{ items: Vessel[]; count: number }>(API.VESSEL, '', false, {
    ids: vesselIds,
    page: 1,
    limit: vesselIds.length,
  });

  return response.data.items;
};

export const getEquipment = async (): Promise<Equipment[]> => {
  const response = await getRequest<{ items: Equipment[]; count: number }>(
    API.VESSEL,
    '/equipment',
    true,
    {
      limit: 1000,
      page: 1,
    },
  );

  return response.data.items;
};

export const checkVesselEmailExists = async (email: string): Promise<boolean> => {
  const response = await getRequest<boolean>(API.VESSEL, '/email', false, {
    email,
  });

  return response.data;
};

export const deleteVessel = async (vesselId: string): Promise<void> => {
  await deleteRequest(API.VESSEL, ``, undefined, false, { id: vesselId }, vesselId);
};

/** "No limit", as a limit — vessel requires the parameter, and a vessel has a few plans. */
export const ALL_MAINTENANCES_LIMIT = 99999;

export const getMaintenances = async (limit = 1000) => {
  const response = await getRequest<{ items: Maintenance[]; count: number }>(
    API.VESSEL,
    '/maintenance',
    true,
    {
      limit,
      page: 1,
    },
  );

  return response.data;
};

/**
 * Every activity generator on the vessel, in one request.
 *
 * There is no generators list of its own — vessel exposes `GET /activity/{id}` and
 * nothing else — but `GET /maintenance` inlines each plan's generators as its
 * `*_tasks_templates`, which is the same record the by-id endpoint answers with. So the
 * maintenance list, asked for whole, is the generators list.
 *
 * Its one blind spot: the templates are joined on `maintenance_id`, so a generator
 * created without a plan is in no maintenance and cannot appear here. Callers that need
 * a specific id still have `getSingleActivityGenerator` to fall back on.
 */
export const getAllActivityGenerators = async (): Promise<ActivityGenerator[]> => {
  const { items } = await getMaintenances(ALL_MAINTENANCES_LIMIT);

  return items.flatMap((maintenance) => [
    ...(maintenance.repeatable_tasks_templates ?? []),
    ...(maintenance.single_tasks_templates ?? []),
  ]);
};

export const createMaintenance = async (body: CreateMaintenancePayload) => {
  const response = await postRequest<Maintenance, CreateMaintenancePayload>(
    API.VESSEL,
    '/maintenance',
    body,
  );
  return response.data;
};

/** The plan a vessel gets when it has none and something needs one. */
export const DEFAULT_MAINTENANCE_TITLE = 'Default Maintenance';

/**
 * The maintenance plan to file an activity generator under, created if the vessel has none.
 *
 * `POST /activity` validates `maintenance_id` as `required,uuid`, so a vessel without a
 * plan cannot have a repeatable task at all — and nothing backend-side ever creates one:
 * the plan is the frontend's to make at vessel creation. A vessel that missed that step
 * (created before the call existed, seeded, or a create that quietly failed) would stay
 * broken for good, which is why this resolves lazily instead of trusting a list to be
 * non-empty.
 *
 * Both requests are scoped by the active `X-Service-Id` — `POST /maintenance` ignores the
 * body's `vessel_id` and takes the vessel from the header — so the plan always belongs to
 * the vessel the caller is working in.
 */
export const ensureMaintenanceId = async (vesselId: string): Promise<string> => {
  const { items } = await getMaintenances();

  if (items.length) {
    return items[0].id;
  }

  const created = await createMaintenance({
    title: DEFAULT_MAINTENANCE_TITLE,
    vessel_id: vesselId,
  });

  return created.id;
};

export const getSingleActivityGenerator = async (
  generatorId: string,
): Promise<ActivityGenerator> => {
  const response = await getRequest<ActivityGenerator>(API.VESSEL, `/activity/${generatorId}`);
  return response.data;
};

export const createActivityGenerator = async (
  body: CreateActivityGeneratorPayload,
): Promise<ActivityGenerator> => {
  const response = await postRequest<ActivityGenerator, CreateActivityGeneratorPayload>(
    API.VESSEL,
    '/activity',
    body,
  );
  return response.data;
};

export const updateActivityGenerator = async (
  body: ActivityGenerator,
): Promise<ActivityGenerator> => {
  const response = await putRequest<ActivityGenerator, ActivityGenerator>(
    API.VESSEL,
    `/activity`,
    body,
  );
  return response.data;
};

export type DeleteActivityMode = 'future' | 'this_and_following';

export const deleteActivityGenerator = async (
  generatorId: string,
  mode: DeleteActivityMode = 'future',
  itemIds?: string[],
): Promise<void> => {
  await deleteRequest(API.VESSEL, `/activity`, {
    id: generatorId,
    mode,
    ...(itemIds && { item_ids: itemIds }),
  });
};

export const createDailyLog = async (log: DailyLog): Promise<DailyLog> => {
  const response = await postRequest<DailyLog, DailyLog>(API.VESSEL, '/daily', log);
  return response.data;
};

export const editDailyLog = async (log: DailyLog, id: string) => {
  await putRequest(API.VESSEL, `/daily/${id}`, log);
};

export const getDailyLogs = async (): Promise<DailyLog[]> => {
  const response = await getRequest<{ items: DailyLog[]; count: number }>(
    API.VESSEL,
    '/daily',
    false,
    {
      limit: 1000,
      page: 1,
    },
  );
  return response.data.items;
};

export const getDailyLogByDate = async (date: string): Promise<DailyLog | undefined> => {
  const response = await getRequest<DailyLog>(API.VESSEL, '/daily/date', false, {
    date,
  });
  return response.data;
};

export const getDailyLogPdf = async (logId: string, timeZone?: string): Promise<Blob> => {
  return getRequestBlob(API.VESSEL, `/daily/${logId}/pdf`, { timezone: timeZone });
};

export const signDailyLog = async (logId: string) => {
  await patchRequest(API.VESSEL, `/daily/${logId}/sign`, undefined);
};

export const deleteDailyLog = async (logId: string): Promise<void> => {
  await deleteRequest(API.VESSEL, `/daily/${logId}`);
};
